import React, { useEffect, useState, useMemo } from 'react';
import styles from './EditModal.module.css';
import { Modal, Form, App } from 'antd';
import type { Color } from 'antd/es/color-picker';
import { logger } from '../../services/logger.client';
import { useStableCallback } from '../../hooks/hookUtils';
import type { EditContext, Image, ItemWithId, ModalInternalState, AnyFormConfig, StoreState } from '../../types/types';
import type { AnyItem } from '../../types/types';
import EditModalImage from './EditModalImage';
import EditModalForm from './EditModalForm';
import EditModalFooter from './EditModalFooter';

// Helper to get a display name from any item type
const getItemName = (item: AnyItem | Partial<AnyItem>): string => {
  if ('name' in item && typeof item.name === 'string') return item.name;
  if ('title' in item && typeof item.title === 'string') return item.title;
  if ('plotName' in item && typeof item.plotName === 'string') return item.plotName;
  return 'Item';
};


export interface TypedEditModalProps {
  config: AnyFormConfig;
  context: EditContext<AnyItem>;
  isOpen: boolean;
  onClose: () => void;
  /** Stable reference to useGlobalState.getState for imperative state reads at action time */
  getState: () => StoreState;
  /** Reactive image library data from the image library store */
  imageLibrary: Image[];
  /** Opens the image-select modal; callback receives the chosen image ID */
  showImageSelectModal: (cb: (id: number) => void) => void;
  /** Navigates to the map and highlights the given item */
  onViewOnMap: (item: ItemWithId | null, itemType: string | null) => void;
  /** Heals the given party member to full HP */
  onHealToFull: (item: ItemWithId | null) => void;
}

const TypedEditModal = ({
  config,
  context,
  isOpen,
  onClose,
  getState,
  imageLibrary,
  showImageSelectModal,
  onViewOnMap,
  onHealToFull,
}: TypedEditModalProps) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const isAdding = !context.item;
  const [internalState, setInternalState] = useState<ModalInternalState>({});
  const [tempImageId, setTempImageId] = useState<number | null>(null);
  const [tempSymbolImageId, setTempSymbolImageId] = useState<number | null>(null);

  const imageMap = useMemo(() => new Map((imageLibrary ?? []).map((img: Image) => [img.id, img.imageUrl])), [imageLibrary]);

  useEffect(() => {
    if (context && config) {
      const initialItem = context.item ?? null;
      const currentState = getState();

      let formValues: Partial<AnyItem> = { ...initialItem };
      let newInternalState: ModalInternalState = {};

      if (config.onOpen) {
        const { formValues: extraFormValues, internalState: openInternalState } = (config.onOpen as (item: AnyItem | null, state: StoreState) => { formValues?: Partial<AnyItem>, internalState?: Partial<ModalInternalState> })(initialItem, currentState) || {};
        if (extraFormValues) formValues = { ...formValues, ...extraFormValues };
        if (openInternalState) newInternalState = { ...newInternalState, ...openInternalState };
      }

      form.setFieldsValue(formValues);
      setInternalState(newInternalState);
      setTempImageId(null);
      setTempSymbolImageId(null);
    }
  }, [context, config, form, getState]);

  // Using useStableCallback to ensure handleClose has a stable reference while always
  // having access to the latest form and onClose values. This prevents unnecessary
  // re-renders of child components that receive this callback as a prop.
  const handleClose = useStableCallback(() => {
    form.resetFields();
    setInternalState({});
    setTempImageId(null);
    setTempSymbolImageId(null);
    onClose();
  });

  // Using useStableCallback for handleSave to maintain a stable reference while ensuring
  // access to the latest config, context, form, and internal state values. This prevents
  // unnecessary re-renders of the footer component that receives this callback.
  const handleSave = useStableCallback(async () => {
    try {
      const formValues = await form.validateFields();
      // Access global state snapshot at save time for any transformations the config may need
      const currentState = getState();

      if (formValues.color && typeof formValues.color !== 'string') {
        formValues.color = (formValues.color as Color).toHexString();
      }

      let itemToSave: AnyItem | Partial<AnyItem> | null;

      if (isAdding) {
        itemToSave = config.createItemFromForm ? config.createItemFromForm(formValues, currentState) : formValues;
      } else {
        itemToSave = { ...context.item, ...formValues };
      }

      if (!itemToSave) {
        message.error('Failed to process item. Please check the form data.');
        return;
      }

      if (tempImageId) (itemToSave as ItemWithId).imageId = tempImageId;
      if (tempSymbolImageId) (itemToSave as ItemWithId).symbolImageId = tempSymbolImageId;

      if (config.onSave) {
        const transformedItem = await (config.onSave as (item: AnyItem | Partial<AnyItem>, isNew: boolean, internalState: ModalInternalState, state: StoreState) => Promise<Partial<AnyItem>> | void | Partial<AnyItem>)(itemToSave, isAdding, internalState, currentState);
        Object.assign(itemToSave, transformedItem ?? {});
      }

      const finalItem = { ...itemToSave, ...internalState };

      if (isAdding && config.addAction) {
         
        const { id: _, ...newItemData } = finalItem;
        await (config.addAction as (item: Partial<AnyItem>) => Promise<void> | void)(newItemData);
      } else if (!isAdding && config.updateAction) {
        await (config.updateAction as (item: AnyItem) => Promise<void> | void)(finalItem as AnyItem);
      }

      const successMessage = isAdding ? `"${getItemName(finalItem)}" has been added.` : `"${getItemName(context.item as AnyItem)}" has been updated.`;
      message.success(successMessage);
      context.onSave?.(finalItem as AnyItem);
      handleClose();
    } catch (error) {
      message.error('Please fill out all required fields.');
      logger.error({ error, context, formValues: form.getFieldsValue() }, 'Edit Modal validation failed on save.');
    }
  });

  // Stable callbacks for image selection to prevent unnecessary re-renders
  const handleSetImage = useStableCallback(() => showImageSelectModal((imageId) => setTempImageId(imageId)));
  const handleSetSymbolImage = useStableCallback(() => showImageSelectModal((imageId) => setTempSymbolImageId(imageId)));

  const item = context.item ?? ({} as AnyItem);

  return (
    <Modal
      title={isAdding ? `Add New ${config.title}` : `Edit ${getItemName(item)}`}
      open={isOpen}
      onCancel={handleClose}
      footer={<EditModalFooter config={config} item={item} itemType={context.type} isAdding={isAdding} onClose={handleClose} onSave={handleSave} onSetImage={handleSetImage} onSetSymbolImage={handleSetSymbolImage} onViewOnMap={onViewOnMap} onHealToFull={onHealToFull} />}
      width={config.width ?? 600}
      destroyOnHidden
    >
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} autoComplete="off" onValuesChange={config.onValuesChange ? (...args) => config.onValuesChange!(...args, form) : undefined}>
        <div className={styles.content}>
          {config.buttons?.setImage && (
            <EditModalImage
              imageId={tempImageId ?? ('imageId' in item ? item.imageId as number | null : null)}
              imageMap={imageMap}
              name={getItemName(item)}
              onSetImage={handleSetImage}
            />
          )}
          <EditModalForm
            config={config}
            currentItem={item}
            form={form}
            internalState={internalState}
            setInternalState={setInternalState}
            getState={getState}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default TypedEditModal;
